import './connector.css'

import {
	IonButton,
	IonInput,
	IonItem,
} from '@ionic/react'
import {
	InputInputEventDetail,
	IonInputCustomEvent,
} from '@ionic/core'
import React, { useEffect, useRef, useState } from 'react'

import {
	resolveNetballConnectFixtureUrl,
	NETBALL_CONNECT_FIXTURE_URL,
	NcResolvedResult,
} from './netballConnectResolver'

export type NetballConnectTeamCardProps = {
	compName: string
	divName: string
	teamName: string
	logoUrl?: string
	onClick?: (event: React.MouseEvent) => void
}

export const NetballConnectTeamCard = (props: NetballConnectTeamCardProps) => {
	const { compName, divName, teamName, logoUrl, onClick } = props

	const content = (
		<>
			{
				logoUrl ? (
					<img src={logoUrl} className="nc-team-picker" alt="" />
				) : null
			}
			<div className="nc-team-text">
				<div className="nc-team-name">
					{teamName}
				</div>
				<div className="nc-team-org">
					{compName}
				</div>
				<div className="nc-team-div">
					{divName}
				</div>
			</div>
		</>
	)

	if (onClick) {
		return (
			<IonButton
				className="nc-team-button"
				fill="outline"
				onClick={onClick}
			>
				{content}
			</IonButton>
		)
	}

	return (
		<div className="nc-team-card">
			{content}
		</div>
	)
}

type NetballConnectUrlPickerProps = {
	actionLabel: string
	onUseTeam: (result: NcResolvedResult) => void | Promise<void>
	onResolvedResult?: (result: NcResolvedResult) => void | Promise<void>
	onResolvedChange?: (hasResult: boolean) => void
	successMessage?: string
	fallbackYear?: number
}

export const NetballConnectUrlPicker = (props: NetballConnectUrlPickerProps) => {
	const { actionLabel, onUseTeam, onResolvedResult, onResolvedChange, successMessage, fallbackYear } = props

	const [pastedUrl, setPastedUrl] = useState('')
	const [resolvedResult, setResolvedResult] = useState<NcResolvedResult | null>(null)
	const [error, setError] = useState<string>()
	const [status, setStatus] = useState<string>()
	const [isResolving, setIsResolving] = useState(false)
	const [isUsingTeam, setIsUsingTeam] = useState(false)

	const lastResolvedUrl = useRef('')
	const lastSharedResultKey = useRef('')
	const debounceTimer = useRef<ReturnType<typeof setTimeout>>()

	const onChangeUrl = (event: IonInputCustomEvent<InputInputEventDetail>) => {
		const value = event.detail.value ?? ''
		setPastedUrl(value)
		setError(undefined)
		setStatus(undefined)
		setResolvedResult(null)
		onResolvedChange?.(false)
	}

	const onReset = () => {
		setPastedUrl('')
		setError(undefined)
		setStatus(undefined)
		setResolvedResult(null)
		lastResolvedUrl.current = ''
		onResolvedChange?.(false)
	}

	const resolveUrl = async (url: string) => {
		if (!url.trim()) return
		lastResolvedUrl.current = url
		setIsResolving(true)
		setError(undefined)
		setStatus(undefined)

		try {
			const result = await resolveNetballConnectFixtureUrl(url, fallbackYear)
			setResolvedResult(result)
			onResolvedChange?.(true)

			const resultKey = `${result.selection.orgKey}:${result.selection.competitionKey}:${result.competitionData.year}`
			if (resultKey !== lastSharedResultKey.current) {
				lastSharedResultKey.current = resultKey
				try {
					await onResolvedResult?.(result)
				} catch (err) {
					console.warn('NetballConnectUrlPicker onResolvedResult failed:', err)
				}
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'That NetballConnect URL could not be resolved.'
			setResolvedResult(null)
			setError(message)
		} finally {
			setIsResolving(false)
		}
	}

	// Auto-resolve after input settles — short delay for paste, longer for typing
	useEffect(() => {
		if (!pastedUrl.trim() || pastedUrl === lastResolvedUrl.current) return

		const looksLikeUrl = pastedUrl.includes('://') || pastedUrl.includes('.com')
		if (!looksLikeUrl) return

		clearTimeout(debounceTimer.current)
		debounceTimer.current = setTimeout(() => {
			void resolveUrl(pastedUrl)
		}, 400)

		return () => clearTimeout(debounceTimer.current)
	}, [pastedUrl]) // eslint-disable-line react-hooks/exhaustive-deps

	const onUseResolvedTeam = async () => {
		if (!resolvedResult) {
			return
		}

		setIsUsingTeam(true)
		setError(undefined)
		setStatus(undefined)

		try {
			await onUseTeam(resolvedResult)
			if (successMessage) {
				setStatus(successMessage)
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'That team could not be used just yet.'
			setError(message)
		} finally {
			setIsUsingTeam(false)
		}
	}

	return (
		<div className="nc-url-picker">
			{
				!resolvedResult ? (
					<>
						<IonItem className="nc-url-input-item">
							<IonInput
								value={pastedUrl}
								type="url"
								placeholder={`${NETBALL_CONNECT_FIXTURE_URL}?...`}
								onIonInput={onChangeUrl}
								label="URL"
							/>
						</IonItem>

						<IonButton
							fill="outline"
							onClick={() => {
								void resolveUrl(pastedUrl)
							}}
							disabled={!pastedUrl.trim() || isResolving}
						>
							{isResolving ? 'Checking...' : 'Check URL'}
						</IonButton>
					</>
				) : null
			}

			{
				error ? (
					<p className="connector-error">
						{error}
					</p>
				) : null
			}

			{
				status ? (
					<p className="connector-success">
						{status}
					</p>
				) : null
			}

			{
				resolvedResult ? (
					<div className="nc-url-picker-result">
						<NetballConnectTeamCard
							compName={resolvedResult.selection.compName}
							divName={resolvedResult.selection.divName}
							teamName={resolvedResult.selection.teamName}
							logoUrl={resolvedResult.selection.logoUrl}
						/>
						<IonButton
							expand="block"
							onClick={() => {
								void onUseResolvedTeam()
							}}
							disabled={isUsingTeam}
						>
							{isUsingTeam ? 'Working...' : actionLabel}
						</IonButton>
						<IonButton
							fill="clear"
                            size="small"
							className="nc-reset-button"
							onClick={onReset}
						>
							Reset Form
						</IonButton>
					</div>
				) : null
			}
		</div>
	)
}
